import { useRef, useState } from "react";
import { Stock, SearchApiParams } from "../types";
import stockListApi from "../api/stockListApi";
import { Severity } from "../../../type";

const limit = 10;

/**
 * 在庫情報一覧取得
 */
export default function useStockList(showAlert: (msg: string, severity?: Severity) => void) {
  const [total, setTotal] = useState(0); // 在庫のレコードの総数
  const [stockList, setStockList] = useState<Stock[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [storehouseUpdated, setStorehouseUpdated] = useState('');
  const [offset, setOffset] = useState(0);
  const isFastLoading = useRef(false); // 二重読み込み防止
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false); // スクロール最下層に達した時、値を変更して発火させるイベント
  const [init, setInit] = useState(false);

  const get = async (searchParams: SearchApiParams) => {
    isFastLoading.current = true;
    setIsLoading(true);
    try {
      // offset, limitは必須なのでparamsに付け足す。
      const res = await stockListApi({
        ...searchParams,
        ...{
          offset: 0,
          limit,
        },
      });
      const sList = res.stocks;
      setTotal(res.total);
      setStorehouseUpdated(res.storehouseUpdated);
      setStockList(structuredClone(sList));
      setOffset(sList.length);
    } catch (err) {
      console.error(err);
      showAlert('在庫一覧情報の取得に失敗しました。', 'error');
    } finally {
      isFastLoading.current = false;
      setIsLoading(false);
    }
  };

  const getMore = async (searchParams: SearchApiParams) => {
    if (stockList.length >= total || !init) {
      return;
    }

    isFastLoading.current = true;
    setIsMoreLoading(true);
    try {
      const res = await stockListApi({
        limit,
        offset,
        ...searchParams,
      });
      setTotal(res.total);
      setStockList((prev) => [...prev, ...res.stocks]);
      setOffset((prev) => prev + res.stocks.length);
    } catch (err) {
      console.error(err);
      showAlert('在庫一覧情報の取得に失敗しました。', 'error');
    } finally {
      isFastLoading.current = false;
      setIsMoreLoading(false);
    }
  };

  return [
    { total, stockList, storehouseUpdated, isLoading, isMoreLoading, isFastLoading, fireMoreLoadEvent },
    { setStockList, get, getMore, setFireMoreLoadEvent, setInit, setTotal },
  ] as const;
}
